import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import { line, surface, radius, shadow, font, bp, transition, cardStyle } from 'features/dashboard/tokens'
import ComplianceScoreExplainer from 'features/compliance/ComplianceScoreExplainer'
import { scoreColour } from 'features/dashboard/charts/scoreColour'

const ACCENT = '#D24724'

const formatPct = v => v === null || v === undefined ? 'No score' : `${Math.round(v)}%`

/**
 * Portfolio View — the focused, single-portfolio identity layout. Three
 * regions:
 *   - Identity hero (big terracotta tile with score donut)
 *   - KPI row (Buildings · At risk · Accountable people)
 *   - Domain completeness by Estate / BP bar matrix
 *
 * Click an estate or BP row in the bar matrix → drills one level deeper.
 */
const roleLabel = (role) => (role || 'member')
  .split('_')
  .map(w => w.charAt(0).toUpperCase() + w.slice(1))
  .join(' ')

const PortfolioView = ({
  portfolioName, portfolioMeta, score, kpis = {},
  entityType, entityId,
  domainByEntity = [],
  domains = [],
  onDrill,
  metadata = {},
  trail = [],
  onTrailJump,
  drillTarget,
  onDrillDown,
}) => {
  const ancestors = trail.slice(0, -1)
  const navigate = useNavigate()
  const isEstate = entityType === 'estate'
  const managePath = entityId
    ? (isEstate ? `/estates/${entityId}` : entityType === 'portfolio' ? `/portfolios/${entityId}` : null)
    : null
  const manageLabel = isEstate ? 'Manage estate' : 'Manage portfolio'
  return (
  <PortfolioGrid>
    <PortfolioMain>
      <Hero>
        <HeroBody>
          <Trail>
            {ancestors.map((seg, idx) => (
              <React.Fragment key={`${seg.type}-${seg.id}`}>
                <TrailChip onClick={() => onTrailJump?.(seg)}>{seg.label}</TrailChip>
                <TrailSep>›</TrailSep>
              </React.Fragment>
            ))}
            {drillTarget && onDrillDown && (
              <DrillPill onClick={onDrillDown} title={`See all ${drillTarget.label} in this scope`}>
                Drill into {drillTarget.label} · {drillTarget.count} →
              </DrillPill>
            )}
          </Trail>
          <IdentityEyebrow>{portfolioMeta?.eyebrow || 'Portfolio'}</IdentityEyebrow>
          <IdentityName>{portfolioName}</IdentityName>
          <IdentityMeta>{portfolioMeta?.line}</IdentityMeta>

          {managePath && (
            <HeroLinks>
              <HeroLink onClick={() => navigate(managePath)}>{manageLabel} ↗</HeroLink>
            </HeroLinks>
          )}

          <HeroStatRow>
            <HeroStat>
              <HeroStatValue>{formatPct(score)}</HeroStatValue>
              <HeroStatLabel>average</HeroStatLabel>
              <HeroExplainer>
                <ComplianceScoreExplainer align='right' />
              </HeroExplainer>
            </HeroStat>
            <HeroStatDivider />
            <HeroStat>
              <HeroStatValue>{kpis.buildings ?? 0}</HeroStatValue>
              <HeroStatLabel>Building Passports</HeroStatLabel>
            </HeroStat>
            <HeroStatDivider />
            <HeroStat>
              <HeroStatValue $tint={(kpis.atRisk || 0) > 0 ? '#FFD8C8' : '#fff'}>
                {kpis.atRisk ?? 0}
              </HeroStatValue>
              <HeroStatLabel>at risk</HeroStatLabel>
            </HeroStat>
          </HeroStatRow>
        </HeroBody>

        <HeroDonut>
          <Donut size={140} stroke={13} score={score} />
        </HeroDonut>
      </Hero>

      <Section>
        <SectionHead>
          <SectionEyebrow>Domain completeness by entity</SectionEyebrow>
          <SectionHint>Click an entity to drill in</SectionHint>
        </SectionHead>
        {domainByEntity.length === 0 ? (
          <EmptyDomain>
            No estates or Building Passports in this portfolio yet.
          </EmptyDomain>
        ) : (
          <DomainList>
            {domainByEntity.map(entity => (
              <DomainBlock
                key={entity.id}
                onClick={() => onDrill?.(entity)}
                role='button'
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onDrill?.(entity) }}
              >
                <DomainHeader>
                  <DomainName>{entity.name}</DomainName>
                  <DomainMetaRight>
                    {entity.bpCount > 0 && <DomainMetaItem>{entity.bpCount} Building Passport{entity.bpCount === 1 ? '' : 's'}</DomainMetaItem>}
                    {entity.lead && <DomainMetaItem>lead {entity.lead}</DomainMetaItem>}
                  </DomainMetaRight>
                </DomainHeader>
                <BarRow>
                  {domains.map(d => {
                    const cell = (entity.domains || []).find(x => x.taskDomainId === d.id)
                    const pct = cell?.score
                    const tint = d.colour || scoreColour(pct)
                    return (
                      <Bar key={d.id}>
                        <BarTrack>
                          <BarFill style={{
                            width: `${Math.max(2, pct ?? 0)}%`,
                            background: pct === null || pct === undefined ? line.base : tint,
                          }} />
                          {pct !== null && pct !== undefined && (
                            <BarLabel>{Math.round(pct)}<small>%</small></BarLabel>
                          )}
                        </BarTrack>
                        <BarTitle>{d.short || d.name}</BarTitle>
                      </Bar>
                    )
                  })}
                </BarRow>
              </DomainBlock>
            ))}
          </DomainList>
        )}
      </Section>
    </PortfolioMain>

    <PortfolioRail>
      {managePath && (
        <RailCard>
          <RailHead>{isEstate ? 'Estate' : 'Portfolio'}</RailHead>
          <ManageLine>
            {isEstate
              ? 'Open the estate page to manage its buildings and members.'
              : 'Open the portfolio page to manage its estates and buildings.'}
          </ManageLine>
          <ManageButton type='button' onClick={() => navigate(managePath)}>
            <LaunchRoundedIcon style={{ fontSize: 16 }} />
            {manageLabel}
          </ManageButton>
        </RailCard>
      )}
      <RailCard>
        <RailHead>At a glance</RailHead>
        <RailGrid>
          <RailStat>
            <RailStatValue>{metadata.buildings_count ?? kpis.buildings ?? 0}</RailStatValue>
            <RailStatLabel>Building Passports</RailStatLabel>
          </RailStat>
          <RailStat>
            <RailStatValue>{metadata.estates_count ?? 0}</RailStatValue>
            <RailStatLabel>Estates</RailStatLabel>
          </RailStat>
          <RailStat>
            <RailStatValue>{metadata.units_count ?? 0}</RailStatValue>
            <RailStatLabel>Unit Passports</RailStatLabel>
          </RailStat>
          <RailStat>
            <RailStatValue>{metadata.people?.length ?? 0}</RailStatValue>
            <RailStatLabel>Accountable</RailStatLabel>
          </RailStat>
        </RailGrid>
      </RailCard>

      {isEstate && Array.isArray(metadata.buildings) && metadata.buildings.length > 0 && (
        <RailCard>
          <RailHead>Buildings in this estate</RailHead>
          <EntityList>
            {metadata.buildings.map(b => (
              <EntityRow key={b.id} type='button' onClick={() => navigate(`/bp-list/${b.id}`)}>
                <EntityName>{b.name || 'Building Passport'}</EntityName>
                <EntityMeta>
                  {[b.city, b.unitsCount ? `${b.unitsCount} unit${b.unitsCount === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ') || '—'}
                </EntityMeta>
              </EntityRow>
            ))}
          </EntityList>
        </RailCard>
      )}

      {isEstate && Array.isArray(metadata.estateMembers) && metadata.estateMembers.length > 0 && (
        <RailCard>
          <RailHead>Estate members</RailHead>
          <MemberList>
            {metadata.estateMembers.map(m => (
              <MemberRow key={m.userId}>
                <MemberName>{m.name}</MemberName>
                <MemberRole>{roleLabel(m.role)}</MemberRole>
              </MemberRow>
            ))}
          </MemberList>
        </RailCard>
      )}

      {Array.isArray(metadata.cities) && metadata.cities.length > 0 && (
        <RailCard>
          <RailHead>Locations</RailHead>
          <CityList>
            {metadata.cities.map(c => <CityChip key={c}>{c}</CityChip>)}
          </CityList>
        </RailCard>
      )}

      {metadata.flags && (metadata.flags.asbestos + metadata.flags.cladding_issue + metadata.flags.flood_risk) > 0 && (
        <RailCard>
          <RailHead>Risk flags</RailHead>
          {metadata.flags.asbestos > 0 && (
            <RiskLine>⚠️ Asbestos · <strong>{metadata.flags.asbestos}</strong> Building Passports</RiskLine>
          )}
          {metadata.flags.cladding_issue > 0 && (
            <RiskLine>⚠️ Cladding issue · <strong>{metadata.flags.cladding_issue}</strong></RiskLine>
          )}
          {metadata.flags.flood_risk > 0 && (
            <RiskLine>⚠️ Flood risk · <strong>{metadata.flags.flood_risk}</strong></RiskLine>
          )}
        </RailCard>
      )}

      {Array.isArray(metadata.people) && metadata.people.length > 0 && (
        <RailCard>
          <RailHead>Accountable people</RailHead>
          {metadata.people.slice(0, 8).map((p, i) => (
            <PersonRow key={`${p.email}-${i}`}>
              <PersonAvatar>{p.name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'}</PersonAvatar>
              <PersonInfo>
                <PersonName>{p.name}</PersonName>
                <PersonRole>{p.role}{p.bp_count > 1 ? ` · ${p.bp_count} Building Passports` : ''}</PersonRole>
              </PersonInfo>
            </PersonRow>
          ))}
          {metadata.people.length > 8 && (
            <RailMore>+{metadata.people.length - 8} more</RailMore>
          )}
        </RailCard>
      )}
    </PortfolioRail>
  </PortfolioGrid>
)}

export default PortfolioView

const Donut = ({ size = 140, stroke = 14, score }) => {
  const r = (size - stroke) / 2
  const C = 2 * Math.PI * r
  const hasScore = score !== null && score !== undefined
  const safe = hasScore ? Math.max(0, Math.min(100, score)) : 0
  const filled = (safe / 100) * C
  return (
    <DonutWrap style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r}
          fill='none'
          stroke='rgba(255,255,255,0.25)'
          strokeWidth={stroke} />
        {hasScore && (
          <circle cx={size/2} cy={size/2} r={r}
            fill='none'
            stroke='#fff'
            strokeWidth={stroke}
            strokeDasharray={`${filled} ${C}`}
            strokeLinecap='round'
            transform={`rotate(-90 ${size/2} ${size/2})`}
            style={{ transition: 'stroke-dasharray 600ms cubic-bezier(0.22, 0.61, 0.36, 1)' }} />
        )}
      </svg>
      <DonutCenter>
        {hasScore ? formatPct(score) : <DonutEmpty>No score yet</DonutEmpty>}
      </DonutCenter>
    </DonutWrap>
  )
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Hero = styled.section`
  position: relative;
  padding: 24px 28px;
  background: ${ACCENT};
  color: #fff;
  border-radius: ${radius.md};
  box-shadow: ${shadow.lift};
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: center;
  margin-bottom: 18px;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: transparent;
    pointer-events: none;
  }
`

const HeroBody = styled.div` display: flex; flex-direction: column; gap: 6px; min-width: 0; position: relative; `
const HeroDonut = styled.div` position: relative; display: inline-flex; flex-shrink: 0; `

const HeroStatRow = styled.div`
  display: flex; align-items: stretch; gap: 18px;
  margin-top: 14px; padding-top: 14px;
  border-top: 1px solid rgba(255,255,255,0.22);
`

const HeroStat = styled.div` display: flex; flex-direction: column; gap: 2px; `

const HeroExplainer = styled.div` margin-top: 6px; `

const HeroLinks = styled.div`
  display: flex; gap: 14px; margin-top: 12px;
  flex-wrap: wrap;
`

const HeroLink = styled.button`
  font: inherit;
  background: none;
  border: none;
  padding: 0;
  color: #fff;
  font-size: ${font.small};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 4px;
  cursor: pointer;
  opacity: 0.92;
  &:hover { opacity: 1; }
`

const ManageLine = styled.p`
  margin: 0 0 12px;
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
  line-height: 1.4;
`

const ManageButton = styled.button`
  font: inherit;
  width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 14px;
  background: ${ACCENT};
  border: 1px solid ${ACCENT};
  border-radius: ${radius.sm};
  color: #fff;
  font-size: ${font.small};
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: ${transition};
  &:hover { filter: brightness(1.06); }
`

const HeroStatValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.6px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ $tint }) => $tint || '#fff'};
`

const HeroStatLabel = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.75);
  font-weight: 800;
`

const HeroStatDivider = styled.span` width: 1px; background: rgba(255,255,255,0.22); `

const Trail = styled.div`
  display: flex; align-items: center; flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`

const TrailChip = styled.button`
  font: inherit;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.28);
  color: #fff;
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: ${radius.pill};
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.32); }
`

const TrailSep = styled.span`
  color: rgba(255,255,255,0.55);
  font-size: ${font.small};
  font-weight: 700;
`

const DrillPill = styled.button`
  font: inherit;
  margin-left: 6px;
  display: inline-flex; align-items: center; gap: 6px;
  background: #33becc;
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff;
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: ${radius.pill};
  cursor: pointer;
  box-shadow: ${shadow.lift};
  transition: ${transition};
  &:hover { transform: translateY(-1px); filter: brightness(1.08); }
`

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  @media (max-width: ${bp.tablet}) { grid-template-columns: 1fr; }
`

const PortfolioMain = styled.div` min-width: 0; `

const PortfolioRail = styled.aside`
  display: flex; flex-direction: column; gap: 14px;
`

const RailCard = styled.section`
  ${cardStyle}
  padding: 16px 18px;
`

const RailHead = styled.h3`
  margin: 0 0 12px;
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const RailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`

const RailStat = styled.div`
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: ${radius.md};
  display: flex; flex-direction: column; gap: 2px;
`

const RailStatValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
`

const RailStatLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const CityList = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `

const EntityList = styled.div` display: flex; flex-direction: column; gap: 2px; `

const EntityRow = styled.button`
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
  width: 100%; text-align: left; background: transparent; border: none;
  padding: 6px 8px; border-radius: 6px; cursor: pointer;
  &:hover { background: rgba(0, 0, 0, 0.04); }
`

const EntityName = styled.span` font-size: 13px; font-weight: 600; color: #1f2937; `

const EntityMeta = styled.span` font-size: 11px; color: #6b7280; white-space: nowrap; `

const MemberList = styled.div` display: flex; flex-direction: column; gap: 6px; `

const MemberRow = styled.div` display: flex; align-items: baseline; justify-content: space-between; gap: 8px; `

const MemberName = styled.span` font-size: 13px; color: #1f2937; `

const MemberRole = styled.span` font-size: 11px; color: #6b7280; white-space: nowrap; `

const CityChip = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  padding: 4px 10px;
  border-radius: ${radius.pill};
  background: ${({ theme }) => theme.colors.lighterGrey};
  color: ${({ theme }) => theme.colors.haiti};
`

const RiskLine = styled.div`
  font-size: ${font.small};
  color: #b00020;
  padding: 4px 0;
  strong { font-weight: 800; }
`

const PersonRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid ${surface.wash};
  &:last-child { border-bottom: none; }
`

const PersonAvatar = styled.div`
  width: 32px; height: 32px;
  border-radius: ${radius.round};
  display: inline-flex; align-items: center; justify-content: center;
  background: ${ACCENT};
  color: #fff;
  font-size: ${font.caption};
  font-weight: 800;
`

const PersonInfo = styled.div` display: flex; flex-direction: column; min-width: 0; `

const PersonName = styled.span`
  font-size: ${font.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const PersonRole = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const RailMore = styled.div`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  text-align: center;
  padding-top: 8px;
  font-weight: 700;
`

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
  @media (max-width: ${bp.tablet}) { grid-template-columns: 1fr 1fr; }
`

const IdentityCard = styled.section`
  position: relative;
  padding: 22px 24px;
  background: ${ACCENT};
  color: #fff;
  border-radius: ${radius.md};
  overflow: hidden;
  min-height: 180px;
  display: flex; flex-direction: column; gap: 6px;
  box-shadow: ${shadow.lift};
  animation: ${fadeUp} 320ms ease both;
`

const IdentityEyebrow = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.78);
  font-weight: 800;
`

const IdentityName = styled.h2`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.8px;
  line-height: 1.1;
`

const IdentityMeta = styled.span`
  font-size: ${font.body};
  color: rgba(255,255,255,0.78);
`

const IdentityScoreRow = styled.div`
  margin-top: auto;
  display: inline-flex; align-items: baseline; gap: 8px;
`

const IdentityScore = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.hero};
  letter-spacing: -1.6px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

const IdentityScoreLabel = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.78);
  font-weight: 800;
`

const DonutWrap = styled.div`
  position: relative;
  display: inline-flex; align-items: center; justify-content: center;
`

const DonutCenter = styled.span`
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  display: flex; align-items: center; justify-content: center;
  text-align: center;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
  color: #fff;
`

const DonutEmpty = styled.span`
  font-family: system-ui, sans-serif;
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.78);
  text-align: center;
  line-height: 1.2;
  max-width: 80px;
`

const KpiTile = styled.div`
  ${cardStyle}
  display: flex; flex-direction: column; gap: 4px;
  padding: 18px 20px;
  min-height: 180px;
`

const KpiLabel = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const KpiValueRow = styled.div`
  display: inline-flex; align-items: baseline; gap: 6px;
`

const KpiValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.hero};
  letter-spacing: -1.2px;
  color: ${({ $tint, theme }) => $tint || theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

const KpiHint = styled.span`
  margin-top: auto;
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Section = styled.section`
  ${cardStyle}
  padding: 18px 22px;
`

const SectionHead = styled.header`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
`

const SectionEyebrow = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const SectionHint = styled.span`
  font-size: ${font.caption};
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base};
  border-radius: ${radius.pill};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const DomainList = styled.div`
  display: flex; flex-direction: column; gap: 16px;
`

const DomainBlock = styled.div`
  padding: 14px 16px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  cursor: pointer;
  transition: ${transition};
  &:hover {
    border-color: ${line.strong};
    background: ${({ theme }) => theme.colors.lighterGrey};
    transform: translateY(-1px);
    box-shadow: ${shadow.lift};
  }
`

const DomainHeader = styled.div`
  display: flex; justify-content: space-between; align-items: baseline;
  margin-bottom: 10px;
`

const DomainName = styled.div`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const DomainMetaRight = styled.div`
  display: inline-flex; gap: 12px;
`

const DomainMetaItem = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const BarRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  @media (max-width: ${bp.mobile}) { grid-template-columns: repeat(4, 1fr); }
`

const Bar = styled.div`
  display: flex; flex-direction: column; gap: 5px;
`

const BarTrack = styled.div`
  position: relative;
  height: 22px;
  background: ${surface.wash};
  border-radius: ${radius.sm};
  overflow: hidden;
`

const BarFill = styled.div`
  height: 100%;
  border-radius: ${radius.sm};
  transition: width 400ms ease;
`

const BarLabel = styled.span`
  position: absolute;
  inset: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: ${font.caption};
  font-weight: 800;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.18);
  font-variant-numeric: tabular-nums;
  small { font-size: 8px; margin-left: 1px; }
`

const BarTitle = styled.span`
  text-align: center;
  font-size: ${font.caption};
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const EmptyDomain = styled.div`
  padding: 32px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`
